import React, { useState, useRef } from 'react';
import { Plus, Edit2, Trash2, QrCode, Building2 } from 'lucide-react';
import { RealQRCode, qrEncodeToCanvas } from '../../utils/qr';
import { createVisitorCompany, updateVisitorCompany, deleteVisitorCompany } from '../../api/visitors';

const C = {
  bg: '#0a0e17', surface: '#131a2b', elevated: '#1a2235',
  border: 'rgba(255,255,255,0.08)', text: '#e2e8f0', muted: '#64748b',
  subtle: '#94a3b8', green: '#22c55e', red: '#ef4444', blue: '#3b82f6',
  amber: '#f59e0b', teal: '#009688',
};

function VisitorCompanyFormModal({ data, sites, prefilledSite, onSave, onClose }) {
  const [form, setForm] = useState({ siteId: data?.siteId || prefilledSite?.id || '', name: data?.name || '', status: data?.status || 'ACTIVE' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const siteIsLocked = !!data?.id || !!prefilledSite;
  const lockedSiteName = data?.siteName || prefilledSite?.name || '';

  const inp = (k, type = 'text') => (
    <input type={type} style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: `1px solid ${C.border}`, background: C.elevated, color: C.text, fontSize: '13px', fontFamily: 'inherit', boxSizing: 'border-box' }}
      value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} />
  );
  const sel = (k, opts) => (
    <select style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: `1px solid ${C.border}`, background: C.elevated, color: C.text, fontSize: '13px', fontFamily: 'inherit', boxSizing: 'border-box' }}
      value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))}>
      {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );

  async function handleSave() {
    if (!form.siteId) return setErr('Site is required');
    if (!form.name.trim()) return setErr('Company name is required');
    setSaving(true); setErr('');
    try {
      if (data?.id) {
        const updated = await updateVisitorCompany(data.id, { name: form.name.trim(), status: form.status });
        onSave(updated);
      } else {
        const created = await createVisitorCompany({ siteId: form.siteId, name: form.name.trim(), status: form.status });
        onSave(created);
      }
    } catch (e) { setErr(e.response?.data?.error || 'Failed to save'); }
    setSaving(false);
  }

  const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' };
  const modal = { background: C.surface, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '24px', width: '380px', maxWidth: '95vw' };
  const label = { fontSize: '11px', fontWeight: 600, color: C.muted, marginBottom: '5px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.4px' };
  const field = { marginBottom: '16px' };

  return (
    <div style={overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modal}>
        <div style={{ fontSize: '15px', fontWeight: 700, color: C.text, marginBottom: '20px' }}>{data?.id ? 'Edit Visitor Company' : 'Add Visitor Company'}</div>
        {err && <div style={{ background: C.red + '22', border: `1px solid ${C.red}44`, borderRadius: '8px', padding: '10px 12px', marginBottom: '14px', fontSize: '12px', color: C.red }}>{err}</div>}
        <div style={field}>
          <label style={label}>Site *</label>
          {siteIsLocked ? <input style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: `1px solid ${C.border}`, background: C.bg, color: C.subtle, fontSize: '13px', fontFamily: 'inherit', boxSizing: 'border-box', cursor: 'not-allowed' }} value={lockedSiteName} readOnly /> : sel('siteId', [['', '— Select site —'], ...sites.map(s => [s.id, s.name])])}
        </div>
        <div style={field}><label style={label}>Company Name *</label>{inp('name')}</div>
        <div style={field}><label style={label}>Status</label>{sel('status', [['ACTIVE', 'Active'], ['INACTIVE', 'Inactive']])}</div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '4px' }}>
          <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding: '9px 18px', borderRadius: '8px', border: 'none', background: C.teal, color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', opacity: saving ? 0.7 : 1 }}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

export default function VisitorCompaniesView({ visitorCompanies, sites, scopedSiteId, onAdd, onUpdate, onRemove }) {
  const [filterSite, setFilterSite] = useState('');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [delErr, setDelErr] = useState('');

  const APP_URL = window.location.origin;
  const effectiveSiteId = scopedSiteId || filterSite;
  const scopedSite = scopedSiteId ? sites.find(s => s.id === scopedSiteId) : null;

  const filtered = visitorCompanies.filter(vc =>
    (!effectiveSiteId || vc.siteId === effectiveSiteId) &&
    (!search || vc.name.toLowerCase().includes(search.toLowerCase()))
  );

  async function handleDelete(vc) {
    setDeleting(true); setDelErr('');
    try {
      await deleteVisitorCompany(vc.id);
      onRemove(vc.id);
      setConfirmDelete(null);
    } catch (e) { setDelErr(e.response?.data?.error || 'Failed to delete'); }
    setDeleting(false);
  }

  function printQR(vc) {
    const win = window.open('', '_blank', 'width=400,height=500');
    const canvas = document.createElement('canvas');
    qrEncodeToCanvas(`${APP_URL}/?vc=${vc.qrHash}`, canvas, 220).then(() => {
      const img = canvas.toDataURL('image/png');
      const esc = s => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      win.document.write(`<!DOCTYPE html><html><head><title>VRF QR — ${esc(vc.name)}</title><style>
        @page{size:100mm 120mm;margin:0}
        body{margin:0;padding:16mm;background:#fff;display:flex;flex-direction:column;align-items:center;justify-content:center;height:100vh;box-sizing:border-box;font-family:-apple-system,sans-serif}
        h2{font-size:14pt;margin:0 0 4px;text-align:center}
        p{font-size:10pt;color:#666;margin:0 0 12px;text-align:center}
        img{display:block}
        small{font-size:8pt;color:#999;margin-top:10px;text-align:center}
      </style></head><body>
        <h2>${esc(vc.name)}</h2>
        <p>${esc(vc.siteName || '')}</p>
        <img src="${img}" width="220" height="220" />
        <small>Scan to submit a Visit Request</small>
      </body></html>`);
      win.document.close();
      setTimeout(() => { win.print(); }, 600);
    });
  }

  const th = { padding: '10px 14px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: C.muted, borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' };
  const td = { padding: '10px 14px', fontSize: '13px', color: C.text, borderBottom: `1px solid ${C.border}` };

  return (
    <div style={{ padding: '24px', color: C.text }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
        <Building2 size={20} color={C.teal} />
        <div>
          <div style={{ fontSize: '17px', fontWeight: 700, color: C.text }}>Visitor Companies{scopedSite ? ` — ${scopedSite.name}` : ''}</div>
          <div style={{ fontSize: '12px', color: C.muted }}>Companies authorised to submit visit requests per site</div>
        </div>
        <button onClick={() => { setEditTarget(null); setShowModal(true); }} style={{ marginLeft: 'auto', padding: '8px 16px', borderRadius: '8px', border: `1px solid ${C.teal}55`, background: C.teal + '22', color: C.teal, fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Plus size={15} /> Add Company
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {!scopedSiteId && (
          <select style={{ padding: '8px 12px', borderRadius: '8px', border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: '13px', fontFamily: 'inherit' }} value={filterSite} onChange={e => setFilterSite(e.target.value)}>
            <option value="">All Sites</option>
            {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
        <input placeholder="Search company…" style={{ padding: '8px 12px', borderRadius: '8px', border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: '13px', fontFamily: 'inherit', flex: 1, minWidth: '180px' }} value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {/* Table */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '12px', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {(scopedSiteId ? ['Company Name', 'Status', 'QR Code', 'Actions'] : ['Company Name', 'Site', 'Status', 'QR Code', 'Actions']).map(h => <th key={h} style={th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={scopedSiteId ? 4 : 5} style={{ ...td, textAlign: 'center', color: C.muted, padding: '32px' }}>No visitor companies found</td></tr>
            ) : filtered.map(vc => (
              <tr key={vc.id}>
                <td style={{ ...td, fontWeight: 600 }}>{vc.name}</td>
                {!scopedSiteId && <td style={td}>{vc.siteName || vc.siteId}</td>}
                <td style={td}>
                  <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, background: vc.status === 'ACTIVE' ? C.green + '22' : C.muted + '22', color: vc.status === 'ACTIVE' ? C.green : C.muted }}>
                    {vc.status}
                  </span>
                </td>
                <td style={td}>
                  <RealQRCode value={`${APP_URL}/?vc=${vc.qrHash}`} size={48} darkColor="#e2e8f0" lightColor="#131a2b" />
                </td>
                <td style={td}>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    <button onClick={() => printQR(vc)} title="Print QR" style={{ padding: '5px 10px', borderRadius: '7px', border: `1px solid ${C.blue}55`, background: C.blue + '22', color: C.blue, fontSize: '11px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '4px' }}><QrCode size={12} /> Print QR</button>
                    <button onClick={() => { setEditTarget(vc); setShowModal(true); }} title="Edit" style={{ padding: '5px 10px', borderRadius: '7px', border: `1px solid ${C.amber}55`, background: C.amber + '22', color: C.amber, fontSize: '11px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '4px' }}><Edit2 size={12} /> Edit</button>
                    <button onClick={() => { setConfirmDelete(vc); setDelErr(''); }} title="Delete" style={{ padding: '5px 10px', borderRadius: '7px', border: `1px solid ${C.red}55`, background: C.red + '22', color: C.red, fontSize: '11px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '4px' }}><Trash2 size={12} /> Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <VisitorCompanyFormModal
          data={editTarget ? { ...editTarget } : null}
          sites={sites}
          prefilledSite={!editTarget && scopedSite ? scopedSite : null}
          onSave={saved => { editTarget ? onUpdate(saved) : onAdd(saved); setShowModal(false); setEditTarget(null); }}
          onClose={() => { setShowModal(false); setEditTarget(null); }}
        />
      )}

      {/* Confirm Delete */}
      {confirmDelete && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '24px', width: '360px', maxWidth: '95vw' }}>
            <div style={{ fontSize: '15px', fontWeight: 700, color: C.text, marginBottom: '12px' }}>Delete Visitor Company?</div>
            <div style={{ fontSize: '13px', color: C.subtle, marginBottom: '16px' }}>This will remove <strong style={{ color: C.text }}>{confirmDelete.name}</strong> and its QR code. This cannot be undone.</div>
            {delErr && <div style={{ background: C.red + '22', border: `1px solid ${C.red}44`, borderRadius: '8px', padding: '10px', marginBottom: '12px', fontSize: '12px', color: C.red }}>{delErr}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => setConfirmDelete(null)} style={{ padding: '9px 18px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
              <button onClick={() => handleDelete(confirmDelete)} disabled={deleting} style={{ padding: '9px 18px', borderRadius: '8px', border: 'none', background: C.red, color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', opacity: deleting ? 0.7 : 1 }}>{deleting ? 'Deleting…' : 'Delete'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
